import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import L from "leaflet"
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet"

import "leaflet/dist/leaflet.css"

// Fix default marker icons (required in many bundlers).
// eslint-disable-next-line no-undef
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png")
})

const DEFAULT_CENTER = [6.5244, 3.3792] // Lagos (fallback)

function parseCoord(raw, isLat) {
  const n = Number(String(raw).trim().replace(",", "."))
  if (!Number.isFinite(n)) return null
  if (isLat && (n < -90 || n > 90)) return null
  if (!isLat && (n < -180 || n > 180)) return null
  return n
}

const ClickToSelect = ({ onSelect, disabled }) => {
  useMapEvents({
    click(e) {
      if (disabled) return
      onSelect({ lat: e.latlng.lat, lng: e.latlng.lng })
    }
  })
  return null
}

const RecenterOnValue = ({ lat, lng }) => {
  const map = useMap()
  const prev = useRef({ lat: null, lng: null })

  useEffect(() => {
    if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return
    const same =
      prev.current.lat === lat &&
      prev.current.lng === lng
    prev.current = { lat, lng }
    if (same) return
    const z = map.getZoom()
    map.setView([lat, lng], z < 13 ? 13 : z, { animate: true })
  }, [lat, lng, map])

  return null
}

const LatLngMapPicker = ({
  value,
  onChange,
  disabled,
  showAddressSearch = true,
  showGeolocation = true,
  showCoordinateInputs = true
}) => {
  const [selected, setSelected] = useState(value || null)
  const [latInput, setLatInput] = useState("")
  const [lngInput, setLngInput] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [searchStatus, setSearchStatus] = useState("")
  const [geoStatus, setGeoStatus] = useState("")

  useEffect(() => {
    setSelected(value || null)
    if (value?.lat != null && value?.lng != null) {
      setLatInput(String(value.lat))
      setLngInput(String(value.lng))
    } else {
      setLatInput("")
      setLngInput("")
    }
  }, [value])

  const center = useMemo(() => {
    if (selected?.lat != null && selected?.lng != null) return [selected.lat, selected.lng]
    return DEFAULT_CENTER
  }, [selected])

  const applyCoords = useCallback(
    (lat, lng) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
      const coords = { lat, lng }
      setSelected(coords)
      setLatInput(String(lat))
      setLngInput(String(lng))
      onChange?.(coords)
    },
    [onChange]
  )

  const handleApplyTypedCoords = () => {
    setSearchStatus("")
    setGeoStatus("")
    const lat = parseCoord(latInput, true)
    const lng = parseCoord(lngInput, false)
    if (lat == null || lng == null) {
      setSearchStatus("Enter valid latitude (−90…90) and longitude (−180…180).")
      return
    }
    applyCoords(lat, lng)
    setSearchStatus("")
  }

  const handleSearchPlace = async () => {
    setSearchStatus("")
    setGeoStatus("")
    const q = searchQuery.trim()
    if (!q) {
      setSearchStatus("Type an address or place name.")
      return
    }
    setSearchStatus("Searching…")
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        q
      )}&limit=1`
      const res = await fetch(url, {
        headers: {
          Accept: "application/json",
          "Accept-Language": "en"
        }
      })
      if (!res.ok) throw new Error("Search failed")
      const data = await res.json()
      if (!Array.isArray(data) || data.length === 0) {
        setSearchStatus("No results. Try a simpler query or add city/country.")
        return
      }
      const lat = Number(data[0].lat)
      const lng = Number(data[0].lon)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        setSearchStatus("Unexpected response. Try again.")
        return
      }
      applyCoords(lat, lng)
      setSearchStatus("Location set from search.")
    } catch {
      setSearchStatus("Could not search right now. Try again or use the map.")
    }
  }

  const handleUseMyLocation = () => {
    setSearchStatus("")
    setGeoStatus("")
    if (!navigator.geolocation) {
      setGeoStatus("Your browser does not support location.")
      return
    }
    setGeoStatus("Getting your location…")
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        applyCoords(pos.coords.latitude, pos.coords.longitude)
        setGeoStatus("Map updated to your current position.")
      },
      () => {
        setGeoStatus("Location permission denied or unavailable.")
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    )
  }

  const featuresOn = !disabled && (showAddressSearch || showGeolocation || showCoordinateInputs)

  return (
    <div className="w-full">
      {featuresOn ? (
        <div className="mb-3 space-y-2">
          {showAddressSearch ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <label className="mb-0.5 block text-[11px] font-medium text-slate-700">
                  Find on map
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleSearchPlace()
                    }
                  }}
                  placeholder="Address, landmark, or area"
                  className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                  disabled={disabled}
                />
              </div>
              <button
                type="button"
                onClick={handleSearchPlace}
                disabled={disabled}
                className="shrink-0 rounded-md bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Search
              </button>
            </div>
          ) : null}
          {showGeolocation ? (
            <div>
              <button
                type="button"
                onClick={handleUseMyLocation}
                disabled={disabled}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Use my current location
              </button>
            </div>
          ) : null}
          {showCoordinateInputs ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="grid flex-1 grid-cols-2 gap-2">
                <div>
                  <label className="mb-0.5 block text-[11px] font-medium text-slate-700">
                    Latitude
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={latInput}
                    onChange={(e) => setLatInput(e.target.value)}
                    placeholder="e.g. 6.5244"
                    className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    disabled={disabled}
                  />
                </div>
                <div>
                  <label className="mb-0.5 block text-[11px] font-medium text-slate-700">
                    Longitude
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={lngInput}
                    onChange={(e) => setLngInput(e.target.value)}
                    placeholder="e.g. 3.3792"
                    className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    disabled={disabled}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleApplyTypedCoords}
                disabled={disabled}
                className="shrink-0 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Apply coordinates
              </button>
            </div>
          ) : null}
          {searchStatus ? (
            <p className="text-[11px] text-slate-600">{searchStatus}</p>
          ) : null}
          {geoStatus ? <p className="text-[11px] text-slate-600">{geoStatus}</p> : null}
        </div>
      ) : null}

      <div className="mb-2">
        <p className="text-[11px] text-slate-600">
          Click the map or drag the pin to fine-tune. Zoom in for a precise point.
          {selected?.lat != null && selected?.lng != null ? (
            <span className="ml-2 text-[11px] font-semibold text-slate-900">
              Pin: {selected.lat.toFixed(6)}, {selected.lng.toFixed(6)}
            </span>
          ) : null}
        </p>
      </div>

      <div className="h-64 w-full overflow-hidden rounded-md border border-slate-200">
        <MapContainer
          center={center}
          zoom={11}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={!disabled}
        >
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {selected?.lat != null && selected?.lng != null ? (
            <RecenterOnValue lat={selected.lat} lng={selected.lng} />
          ) : null}
          {!disabled && (
            <ClickToSelect
              disabled={disabled}
              onSelect={(coords) => applyCoords(coords.lat, coords.lng)}
            />
          )}
          {selected?.lat != null && selected?.lng != null ? (
            <Marker
              position={[selected.lat, selected.lng]}
              draggable={!disabled}
              eventHandlers={{
                dragend: (e) => {
                  const p = e.target.getLatLng()
                  applyCoords(p.lat, p.lng)
                }
              }}
            />
          ) : null}
        </MapContainer>
      </div>
    </div>
  )
}

export default LatLngMapPicker
